import { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import debounce from 'lodash.debounce';
import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Spin } from 'antd';
import taskerCategoriesApi from '@/api/taskerCategories';
import { disabledDate } from '@/utils/publicMethod';
import { calendarIcon, searchIcon } from '@/constants/static';

type TaskerCategoryRow = {
    taskerCategoryId: string;
    categoryName: string;
    priority: number;
    transferHours: number;
    createTime: string;
    updateTime: string;
};

type TaskerCategoryForm = { categoryName: string; priority: number; transferHours: number };

const TaskerCategories = () => {
    const [form] = Form.useForm<TaskerCategoryForm>();
    const [categoryId, setCategoryId] = useState('');
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingRow, setEditingRow] = useState<TaskerCategoryRow | null>(null);
    const [saving, setSaving] = useState(false);
    const { RangePicker } = DatePicker;
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<TaskerCategoryRow[]>([]);
    const [allRows, setAllRows] = useState<TaskerCategoryRow[]>([]);
    const [listParams, setListParams] = useState({
        startTime: '',
        endTime: '',
        keyword: '',
    });

    const debouncedKeyword = useMemo(
        () =>
            debounce((keyword: string) => {
                setListParams(prev => ({ ...prev, keyword }));
            }, 1500),
        []
    );
    const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(e.target.value);
    };

    const getList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await taskerCategoriesApi.getList(listParams);
            setRows(res.data || []);
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        } finally {
            setLoading(false);
        }
    }, [listParams]);

    const getAllRows = useCallback(async () => {
        const res = await taskerCategoriesApi.getList();
        setAllRows(res.data || []);
    }, []);

    useEffect(() => {
        void getList();
    }, [getList]);

    useEffect(() => {
        void getAllRows();
    }, [getAllRows]);

    const dateOnChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
        const start = dates?.[0];
        const end = dates?.[1];
        if (!start || !end) {
            setListParams(prev => ({ ...prev, startTime: '', endTime: '' }));
            return;
        }
        setListParams(prev => ({
            ...prev,
            startTime: dayjs(`${start.format('YYYY-MM-DD')} 00:00:00`).toISOString(),
            endTime: dayjs(`${end.format('YYYY-MM-DD')} 23:59:59`).toISOString(),
        }));
    };

    const closeDeleteModal = () => setOpenDeleteModal(false);

    const openAddModal = async () => {
        form.resetFields();
        setEditingRow(null);
        form.setFieldsValue({
            categoryName: '',
            priority: undefined as unknown as number,
            transferHours: 2,
        });
        try {
            await getAllRows();
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        }
        setShowAddModal(true);
    };

    const openEditModal = async (row: TaskerCategoryRow) => {
        form.resetFields();
        setEditingRow(row);
        form.setFieldsValue({
            categoryName: row.categoryName,
            priority: row.priority,
            transferHours: row.transferHours ?? 2,
        });
        try {
            await getAllRows();
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        }
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setEditingRow(null);
        form.resetFields();
    };

    const saveCategory = async (values: TaskerCategoryForm) => {
        setSaving(true);
        try {
            const params = {
                categoryName: values.categoryName.trim(),
                priority: values.priority,
                transferHours: values.transferHours,
            };
            if (editingRow) {
                await taskerCategoriesApi.update(editingRow.taskerCategoryId, params);
                message.success('Category updated');
            } else {
                await taskerCategoriesApi.create(params);
                message.success('Category created');
            }
            closeAddModal();
            getList();
            getAllRows();
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        } finally {
            setSaving(false);
        }
    };

    const deleteRow = async () => {
        try {
            await taskerCategoriesApi.delete(categoryId);
            getList();
            getAllRows();
            setCategoryId('');
            setOpenDeleteModal(false);
        } catch (error: unknown) {
            message.error(error instanceof Error ? error.message : String(error));
        }
    };

    const validatePriority = (_: unknown, value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(Number(value))) {
            return Promise.reject(new Error('Please enter priority'));
        }
        if (Number(value) < 1) {
            return Promise.reject(new Error('Priority must be greater than 0'));
        }
        if (
            allRows.some(
                row => row.taskerCategoryId !== editingRow?.taskerCategoryId && Number(row.priority) === Number(value)
            )
        ) {
            return Promise.reject(new Error('Priority already exists'));
        }
        return Promise.resolve();
    };

    return (
        <div className="page-main Categories">
            <div className="page-title flex-between">
                Tasker Tier
                <Button
                    className="flex-center"
                    style={{ height: 46, minWidth: 123, padding: '0 16px', borderRadius: 8 }}
                    type="primary"
                    onClick={openAddModal}
                >
                    <img src={require('@/assets/images/add-icon.png')} alt="" /> Add new
                </Button>
            </div>

            <div className="select-bar" style={{ marginBottom: 16 }}>
                <RangePicker
                    className="search-input"
                    placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                    suffixIcon={calendarIcon}
                    separator={'-'}
                    format={'DD/MM/YYYY'}
                    disabledDate={disabledDate}
                    onChange={dateOnChange}
                />
                <Input
                    className="search-input"
                    placeholder="Search by name"
                    onChange={inputChange}
                    prefix={searchIcon}
                />
            </div>

            {loading ? (
                <div className="data_loading flex-center">
                    <Spin />
                </div>
            ) : rows.length ? (
                <div className="Categories">
                    {rows.map(c => (
                        <div className="Categories-item" key={c.taskerCategoryId}>
                            <div className="Categories-item-left">
                                <div className="item-num">{c.priority}</div>
                                <div className="item-base" style={{ marginLeft: 12 }}>
                                    <div className="item-name">{c.categoryName}</div>
                                    <div className="item-taskers" style={{ fontSize: 12, opacity: 0.75 }}>
                                        Priority: {c.priority} · Moves to next tier after: {c.transferHours ?? 2}h ·
                                        Created: {c.createTime ? dayjs(c.createTime).format('YYYY-MM-DD HH:mm') : '-'}
                                    </div>
                                </div>
                            </div>
                            <div className="Categories-item-right">
                                <img
                                    src={require('@/assets/images/edit-icon.png')}
                                    alt="edit"
                                    onClick={() => openEditModal(c)}
                                />
                                <img
                                    src={require('@/assets/images/delete-icon.png')}
                                    alt="delete"
                                    onClick={() => {
                                        setOpenDeleteModal(true);
                                        setCategoryId(c.taskerCategoryId);
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-data flex-center">No data</div>
            )}

            <Modal
                width={408}
                className="tasker-category-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={showAddModal}
                onCancel={closeAddModal}
                footer={null}
            >
                <div className="modal-title">{editingRow ? 'Edit tasker tier' : 'Add tasker tier'}</div>
                <Form
                    form={form}
                    className="tier-form"
                    layout="vertical"
                    onFinish={saveCategory}
                    initialValues={{ categoryName: '' }}
                >
                    <Form.Item
                        label="Name"
                        name="categoryName"
                        rules={[{ required: true, message: 'Please enter name' }]}
                    >
                        <Input placeholder="Category name" maxLength={128} />
                    </Form.Item>
                    <Form.Item
                        label="Priority"
                        name="priority"
                        rules={[{ required: true, message: 'Please enter priority' }, { validator: validatePriority }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            precision={0}
                            placeholder="Number (e.g. lower = higher rank)"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Hours before it moves to the next tier"
                        name="transferHours"
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                            { required: true, message: 'Please enter hours' },
                            {
                                // The combined hours across ALL tiers may not exceed 24 (a job
                                // cycles through every tier within a day). Show a clear red error.
                                validator: (_, value?: number | null) => {
                                    if (value === undefined || value === null) {
                                        return Promise.resolve();
                                    }
                                    if (value < 1) {
                                        return Promise.reject(new Error('Hours must be at least 1'));
                                    }
                                    const othersSum = allRows
                                        .filter(r => r.taskerCategoryId !== editingRow?.taskerCategoryId)
                                        .reduce((sum, r) => sum + (Number(r.transferHours) || 0), 0);
                                    if (othersSum + value > 24) {
                                        return Promise.reject(
                                            new Error(
                                                `Total tier hours can't exceed 24 — other tiers already use ${othersSum}h. Please reduce the hours.`
                                            )
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            precision={0}
                            addonAfter="hours"
                            placeholder="1 - 24"
                        />
                    </Form.Item>
                    <div className="flex-center" style={{ marginTop: 4, columnGap: 16 }}>
                        <Button style={{ width: 132, height: 44 }} onClick={closeAddModal} className="cancel-btn">
                            Cancel
                        </Button>
                        <Button style={{ width: 132, height: 44 }} type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal>

            <Modal
                width={408}
                className="tasker-category-modal tasker-category-delete-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={openDeleteModal}
                onCancel={closeDeleteModal}
                footer={null}
            >
                <div className="modal-title">Delete tasker tier</div>
                <div className="modal-tip">Are you sure you want to delete this?</div>
                <div className="flex-center" style={{ marginTop: 24, columnGap: 16 }}>
                    <Button style={{ width: 132, height: 44 }} onClick={closeDeleteModal} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button style={{ width: 132, height: 44 }} danger type="primary" onClick={deleteRow}>
                        Yes, delete
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default TaskerCategories;
